'use client'

import type { Product } from '@/payload-types'
import { cn } from '@/utilities/cn'
import { usePathname, useRouter } from '@/i18n/routing'
import { useSearchParams } from 'next/navigation'
import React from 'react'

export function VariantSelector({
  product,
  priceBlock,
  onHoverOption,
}: {
  product: Product
  priceBlock?: React.ReactNode
  onHoverOption?: (price: number | null) => void
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const variants = product.variants
  const enableVariants = product.enableVariants

  if (!enableVariants || !Array.isArray(variants) || variants.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      {variants.map((type) => {
        if (!type || typeof type !== 'object') return null

        const options = type.values
        if (!options || !Array.isArray(options) || options.length === 0) return null

        const optionKeyLowerCase = type.name.toLowerCase()
        const selectedValue = searchParams.get(optionKeyLowerCase)
        const isFirst = variants.indexOf(type) === 0

        return (
          <div className="flex flex-col gap-1" key={type.id || type.name}>
            <div className="flex justify-between items-end">
              <span className="text-xs font-mono uppercase tracking-widest text-white/80 font-bold">
                {type.name}
              </span>
              {isFirst && priceBlock}
            </div>
            <div className="flex flex-wrap gap-2">
              {options.map((option) => {
                if (!option || typeof option !== 'object') return null

                const isActive = selectedValue === option.value
                const price = option.price

                const handleSelect = () => {
                  const params = new URLSearchParams(searchParams.toString())
                  params.set(optionKeyLowerCase, option.value)
                  router.replace(`${pathname}?${params.toString()}`, { scroll: false })
                }

                return (
                  <button
                    key={option.id || option.value}
                    onClick={handleSelect}
                    onMouseEnter={() => onHoverOption?.(typeof price === 'number' ? price : null)}
                    onMouseLeave={() => onHoverOption?.(null)}
                    className={cn(
                      'px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg text-xs sm:text-sm font-mono uppercase tracking-normal sm:tracking-widest transition-all duration-300 border text-left max-w-full break-words whitespace-normal',
                      'backdrop-blur-md shadow-sm',
                      isActive
                        ? 'bg-[var(--neon-orange)] border-[var(--neon-orange)] text-black font-bold shadow-[0_0_15px_var(--neon-orange)] sm:scale-105'
                        : 'bg-black/60 border-white/10 text-white/90 hover:border-[var(--neon-orange)] hover:text-white hover:shadow-[0_0_10px_var(--neon-orange)]',
                    )}
                  >
                    {option.value}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
